import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import SumsubWebSdk from '@sumsub/websdk-react';
import { CheckCircle, Warning, ArrowLeft, Sun, Moon } from '@phosphor-icons/react';
import { TFMark } from './TFLogo';
import usePublicTheme from '../hooks/usePublicTheme';

const API = process.env.REACT_APP_BACKEND_URL;

async function fetchAccessToken(externalUserId, kycSession) {
  const r = await fetch(`${API}/api/kyc/access-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-KYC-Session': kycSession || '',
    },
    body: JSON.stringify({ external_user_id: externalUserId }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${r.status}`);
  }
  const data = await r.json();
  return data.token;
}

export default function KycPage() {
  const [mode, toggleTheme] = usePublicTheme();
  const { externalUserId } = useParams();
  const [searchParams] = useSearchParams();
  const trustframeId = searchParams.get('tfid') || '';

  // Session token from URL query (Telegram deep link or web signup redirect),
  // persisted in sessionStorage so it survives re-renders.
  const urlSession = searchParams.get('session') || '';
  const storedSession = typeof window !== 'undefined'
    ? window.sessionStorage.getItem(`kyc_sess_${externalUserId}`) || ''
    : '';
  const kycSession = urlSession || storedSession;

  useEffect(() => {
    if (urlSession && typeof window !== 'undefined') {
      window.sessionStorage.setItem(`kyc_sess_${externalUserId}`, urlSession);
    }
  }, [urlSession, externalUserId]);

  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('pending');
  const [reviewAnswer, setReviewAnswer] = useState(null);

  // Fetch access token on mount
  useEffect(() => {
    let active = true;
    fetchAccessToken(externalUserId, kycSession)
      .then((t) => { if (active) setToken(t); })
      .catch((e) => { if (active) setError(e.message || String(e)); });
    return () => { active = false; };
  }, [externalUserId, kycSession]);

  // Poll status every 5s
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/api/kyc/status/${encodeURIComponent(externalUserId)}`);
        if (!r.ok || !active) return;
        const data = await r.json();
        if (!active) return;
        if (data.kyc_status) setStatus(data.kyc_status);
        if (data.review_answer) setReviewAnswer(data.review_answer);
      } catch {}
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { active = false; clearInterval(id); };
  }, [externalUserId]);

  const expirationHandler = async () => fetchAccessToken(externalUserId, kycSession);

  const retry = () => {
    setError(null);
    setToken(null);
    fetchAccessToken(externalUserId, kycSession)
      .then((t) => setToken(t))
      .catch((e) => setError(e.message || String(e)));
  };

  const showLoading = !token && !error;

  return (
    <div className="min-h-screen bg-tf-bg text-tf-text" data-testid="kyc-page">
      <header className="border-b border-tf-border-muted px-4 sm:px-8 py-3 sm:py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-4 sm:gap-6 min-h-[44px]" data-testid="kyc-home-link">
            <TFMark color="currentColor" size={26} />
            <span className="font-machina font-bold text-[11px] sm:text-xs uppercase" style={{ letterSpacing: '0.95em', paddingLeft: '0.95em' }}>TRUSTFRAME</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={toggleTheme}
              data-testid="kyc-theme-toggle"
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="min-h-[36px] min-w-[36px] px-2 inline-flex items-center justify-center border border-tf-border-muted text-tf-text-2 hover:text-tf-text transition-colors"
            >
              {mode === 'dark' ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
            </button>
            <Link to="/" className="min-h-[44px] px-2 font-mono text-[10px] tracking-[0.2em] uppercase text-tf-text-2 hover:text-tf-text flex items-center gap-2">
              <ArrowLeft size={12} weight="bold" /> Back
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12 md:py-16">
        <div className="mb-6 sm:mb-8">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-tf-primary mb-3">[ .COMPLY. .IDENTITY. ]</p>
          <h1 className="font-heading font-bold text-2xl sm:text-4xl md:text-5xl tracking-tight mb-4 leading-tight">Verify Your Identity</h1>
          <p className="font-mono text-xs sm:text-sm text-tf-text-2 leading-relaxed break-words">
            TrustFrame is regulated for AML compliance. Identity verification is processed by Sumsub —
            an EU-regulated KYC provider. Your data is encrypted and handled in line with GDPR.
          </p>
          {trustframeId && (
            <p className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-tf-text-2 break-all" data-testid="kyc-trustframe-id">
              Account: <span className="text-tf-primary">{trustframeId}</span>
            </p>
          )}
        </div>

        <div className="mb-6 flex items-center gap-3" data-testid="kyc-status-pill">
          {status === 'verified' && (
            <span className="inline-flex items-center gap-2 px-3 py-1 border border-tf-primary text-tf-primary font-mono text-[10px] tracking-[0.2em] uppercase">
              <CheckCircle size={14} weight="bold" /> Verified
            </span>
          )}
          {status === 'rejected' && (
            <span className="inline-flex items-center gap-2 px-3 py-1 border border-tf-danger text-tf-danger font-mono text-[10px] tracking-[0.2em] uppercase">
              <Warning size={14} weight="bold" /> Rejected
            </span>
          )}
          {status === 'pending' && (
            <span className="inline-flex items-center gap-2 px-3 py-1 border border-tf-text-2 text-tf-text-2 font-mono text-[10px] tracking-[0.2em] uppercase">
              <span className="w-2 h-2 bg-tf-text-2 rounded-full animate-pulse" /> Pending
            </span>
          )}
        </div>

        <div className="border border-tf-border-light bg-tf-surface overflow-hidden" data-testid="kyc-sdk-container">
          {showLoading && (
            <div className="p-8 sm:p-12 text-center">
              <div className="w-8 h-8 border border-tf-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="font-mono text-tf-text-2 text-[10px] tracking-[0.3em] uppercase">Initializing verification</p>
            </div>
          )}
          {error && (
            <div className="p-6 sm:p-8 md:p-12">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-tf-danger mb-3">[ .error. ]</p>
              <p className="font-mono text-sm text-tf-text mb-4 break-words" data-testid="kyc-error">{error}</p>
              <button
                onClick={retry}
                data-testid="kyc-retry-btn"
                className="min-h-[44px] px-4 py-2 border border-tf-primary text-tf-primary font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-tf-primary hover:text-white transition-all"
              >
                Retry
              </button>
            </div>
          )}
          {token && !error && (
            <div className="sumsub-wrapper p-1 sm:p-2" data-testid="sumsub-websdk-mount">
              <SumsubWebSdk
                accessToken={token}
                expirationHandler={expirationHandler}
                config={{ lang: 'en' }}
                options={{ addViewportTag: false, adaptIframeHeight: true }}
                onMessage={(t) => console.log('[Sumsub]', t)}
                onError={(e) => console.error('[Sumsub error]', e)}
              />
            </div>
          )}
        </div>

        {status === 'verified' && (
          <div className="mt-6 sm:mt-8 p-5 sm:p-6 border border-tf-primary bg-tf-surface" data-testid="kyc-success">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-tf-primary mb-2">[ .verified. ]</p>
            <h2 className="font-heading font-bold text-xl sm:text-2xl mb-2">Identity confirmed</h2>
            <p className="font-mono text-xs sm:text-sm text-tf-text-2 mb-4 break-words">
              Your TrustFrame account is now fully active. You can return to Telegram or close this page.
            </p>
            <a
              href="https://t.me/TrustframeBot"
              className="inline-block min-h-[44px] px-5 py-2 bg-tf-primary text-white font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-tf-accent transition-all leading-[24px]"
              data-testid="kyc-return-bot"
            >
              Open Telegram Bot
            </a>
          </div>
        )}

        {status === 'rejected' && (
          <div className="mt-6 sm:mt-8 p-5 sm:p-6 border border-tf-danger bg-tf-surface">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-tf-danger mb-2">[ .rejected. ]</p>
            <p className="font-mono text-xs sm:text-sm text-tf-text-2 break-words">
              Verification was not approved. Please contact <a href="mailto:support@trustframe.legal" className="text-tf-primary underline break-all">support@trustframe.legal</a>{reviewAnswer ? ` (${reviewAnswer})` : ''}.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
